import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TextInputComp from "../../../components/customer/TextInputComp";
import MediaUploadComp from "../../../components/MediaUploadComp";
import CommonButton from "../../../components/CommonButton";
import TextAreaComp from "../../../components/customer/TextAreaComp";
import { useUploadFileMutation } from "../../../redux/actions/Admin/ServiceAdmin";
import { ArrowLeft, Loader2 } from "lucide-react";
import { ICreateBrandRequest } from "../../../types/Request/AdminRequest";
import { IBrandResponse } from "../../../types/Response/AdminResponse";
import { useCreateBrandMutation, useUpdateBrandMutation } from "../../../redux/actions/Admin/ProductAdmin";

const AdminBrandAddScreen: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Unpack clean typed context parameter flags if coming via an Edit execution call routing route
    const targetEditData = location.state?.brand as IBrandResponse | undefined;

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [logoUrl, setLogoUrl] = useState("");

    // Hook Mutators setup
    const [createBrand, { isLoading: isCreating }] = useCreateBrandMutation();
    const [updateBrand, { isLoading: isUpdating }] = useUpdateBrandMutation();
    const [uploadFile, { isLoading: isUploadingMedia }] = useUploadFileMutation();

    const isSubmissionProcessing = isCreating || isUpdating || isUploadingMedia;

    // Prefill data entries if editing existing server row parameters mapping matrix
    useEffect(() => {
        if (targetEditData) {
            setName(targetEditData.name || "");
            setDescription(targetEditData.description || "");
            setLogoUrl(targetEditData.logoUrl || "");
        }
    }, [targetEditData]);

    // Intercept image selections, upload directly, and store string URLs into state context
    const handleBrandLogoUploaderStream = async (file: File) => {
        try {
            const response = await uploadFile({
                File: file,
                FolderName: "Brands/Logos"
            }).unwrap();
            
            // Attach resolved remote server URL image parameter explicitly
            setLogoUrl(response.fileUrl);
        } catch (error) {
            console.error("Binary file asset stream upload transmission exception caught:", error);
            alert("Media asset upload pipeline failed. Check template sizes or retry again.");
        }
    };

    const handleFormPayloadSubmission = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim() || isSubmissionProcessing) return;

        const requestPayload: ICreateBrandRequest = {
            name: name.trim(),
            description: description.trim(),
            logoUrl: logoUrl,
        };

        try {
            if (targetEditData) {
                // Edit / Update operational mutation flow
                await updateBrand({ id: targetEditData.id, data: requestPayload }).unwrap();
            } else {
                // Create brand operational mutation flow
                await createBrand(requestPayload).unwrap();
            }
            navigate(-1); // Return safely to history listings board on completion
        } catch (error) {
            console.error("Brand configuration registry write operations rejected:", error);
        }
    };

    return (
        <div className="px-5 bg-white min-h-screen py-6 relative">
            
            {/* Shield Block Lockout Overlay processing spinner loops */}
            {isSubmissionProcessing && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-xs z-50 flex items-center justify-center">
                    <div className="bg-white rounded-2xl p-6 flex items-center gap-3 shadow-xl">
                        <Loader2 className="w-5 h-5 animate-spin text-[#2F5E2E]" />
                        <span className="font-semibold text-sm text-slate-700">
                            {isUploadingMedia ? "Uploading brand logo vector to cloud bucket..." : "Committing transaction parameter logs change sets..."}
                        </span>
                    </div>
                </div>
            )}

            {/* Header Title Section */}
            <div className="mb-6">
                <button
                    onClick={() => navigate(-1)}
                    disabled={isSubmissionProcessing}
                    className="flex items-center gap-2 text-sm font-semibold text-iconColor hover:text-[#2F5E2E] transition-colors mb-2 cursor-pointer disabled:opacity-40"
                >
                    <ArrowLeft className="w-4 h-4" /> Return to Brand Overview List
                </button>
                <h1 className="text-2xl font-bold text-logoHeading">
                    {targetEditData ? `Modify Brand Parameters: ${targetEditData.name}` : "Create Brand Designation Entry"}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                    {targetEditData ? "Edit explicit asset paths references metrics directly inside systemic indices configurations lists." : "Create and manage product brands for your commerce platform"}
                </p>
            </div>
 
            {/* Main Form Fields Container Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
                <form onSubmit={handleFormPayloadSubmission} className="space-y-6">

                    {/* Basic Parameters Info Fields Row */}
                    <div>
                        <h2 className="text-xs font-bold uppercase tracking-wider text-logoHeading mb-3">
                            Identity Profiling Reference
                        </h2>
                        <div className="w-full">
                            <TextInputComp
                                label="Brand Name"
                                value={name}
                                onChangeText={setName}
                                placeholder="Enter corporate vendor naming handle text..."
                                showStar
                                showLabel
                                disabled={isSubmissionProcessing}
                            />
                        </div>
                    </div>

                    {/* Detailed Descriptive Text Box Panel Block */}
                    <div>
                        <h2 className="text-xs font-bold uppercase tracking-wider text-logoHeading mb-3">
                            Brand Descriptive Guidelines
                        </h2>
                        <TextAreaComp
                            value={description}
                            onChangeText={setDescription}
                            rows={4}
                            placeholder="Write comprehensive notes or marketing descriptions concerning target brand configurations entries profiles..."
                            disabled={isSubmissionProcessing}
                        />
                    </div>

                    {/* Media Assets File Streams Selectors Wrapper Blocks */}
                    <div>
                        <h2 className="text-xs font-bold uppercase tracking-wider text-logoHeading mb-3">
                            Brand Visual Communications Marks
                        </h2>

                        <div className="max-w-md">
                            <MediaUploadComp
                                label="Brand Corporate Vector Logo"
                                value={logoUrl}
                                onChange={setLogoUrl}
                                onFileSelect={handleBrandLogoUploaderStream}
                                type="image"
                                showStar
                                aspectRatio="aspect-square"
                  
                            />
                            <p className="text-[11px] text-gray-400 mt-2">
                                Supported formats: PNG, SVG Vector, JPG. Preferred boundary size specs: 1:1 Aspect ratio.
                            </p>
                        </div>
                    </div>

                    {/* Action Panel Confirm/Cancel Buttons Group */}
                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                        <CommonButton
                            text="Discard Forms Changes"
                            variant="outline"
                            rounded="lg"
                            disabled={isSubmissionProcessing}
                            onClick={() => navigate(-1)}
                            className="px-6 py-2 border-gray-200 text-gray-600 font-semibold text-xs"
                        />

                        <CommonButton
                            text={targetEditData ? "Commit Edit Alterations" : "Save Brand Settings Reference"}
                            variant="filled"
                     
                            disabled={isSubmissionProcessing || !name.trim()}
                            className="bg-[#2F5E2E] hover:bg-[#234722] text-white text-xs font-semibold px-6 py-2"
                        />
                    </div>

                </form>
            </div>
        </div>
    );
};

export default AdminBrandAddScreen;